import { useRef, useState } from 'react';
import {
  KpiCard,
  WidgetShell,
  BaseAreaChart,
  BaseBarChart,
  BaseDonutChart,
  MiniSparkline,
  ProgressRing,
  ProgressBar,
  StatusCard,
  SummaryCard,
  Tabs,
  Modal,
  Button,
  ExportButtonGroup,
  DataGrid,
  type DataGridColumn,
} from '@kss/ui';

const area = [
  { month: 'Jan', value: 120 }, { month: 'Feb', value: 180 }, { month: 'Mar', value: 150 },
  { month: 'Apr', value: 220 }, { month: 'May', value: 260 }, { month: 'Jun', value: 310 },
];
const bar = [
  { name: 'Mon', value: 34 }, { name: 'Tue', value: 51 }, { name: 'Wed', value: 28 },
  { name: 'Thu', value: 62 }, { name: 'Fri', value: 47 }, { name: 'Sat', value: 80 }, { name: 'Sun', value: 39 },
];
const donut = [
  { name: 'Direct', value: 420 },
  { name: 'Referral', value: 180 },
  { name: 'Organic', value: 260 },
];
const spark = [12, 18, 14, 22, 28, 31, 26, 30];

interface KpiDetail extends Record<string, unknown> {
  id: number;
  date: string;
  channel: string;
  amount: number;
  orders: number;
}

const revenueDetails: KpiDetail[] = Array.from({ length: 32 }, (_, i) => ({
  id: i + 1,
  date: `2026-04-${String((i % 28) + 1).padStart(2, '0')}`,
  channel: ['Direct', 'Referral', 'Organic', 'Social'][i % 4],
  amount: 8000 + i * 650,
  orders: 3 + (i % 12),
}));

const detailColumns: DataGridColumn<KpiDetail>[] = [
  { accessor: 'id', title: '#', width: 60, sortable: true },
  { accessor: 'date', title: 'Date', sortable: true },
  { accessor: 'channel', title: 'Channel', sortable: true },
  { accessor: 'amount', title: 'Amount (₹)', sortable: true, render: (r) => r.amount.toLocaleString('en-IN'), textAlign: 'right' },
  { accessor: 'orders', title: 'Orders', sortable: true, textAlign: 'right' },
];

function toCsv(rows: KpiDetail[]): string {
  const header = ['id', 'date', 'channel', 'amount', 'orders'];
  return [header.join(','), ...rows.map((r) => header.map((h) => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
}

function download(filename: string, content: string, type = 'text/csv') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Dashboard() {
  const [drill, setDrill] = useState<{ title: string; value: string } | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const openKpi = (title: string, value: string) => setDrill({ title, value });
  const closeKpi = () => setDrill(null);

  const handlePrint = () => {
    const node = printRef.current;
    if (!node) {
      window.print();
      return;
    }
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    win.document.write(`<html><head><title>${drill?.title}</title>
      <style>body{font-family:Inter,system-ui,sans-serif;padding:24px;color:#111827}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #e2e8f0;padding:6px 10px;text-align:left}
      th{background:#f8fafc}
      h1{font-size:20px;margin:0 0 8px}
      h2{font-size:14px;color:#64748b;margin:0 0 16px;font-weight:500}
      </style></head><body>
      <h1>${drill?.title}</h1><h2>Value: ${drill?.value}</h2>
      ${node.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 250);
  };

  const handleExcel = () => {
    download(`${drill?.title?.toLowerCase() ?? 'kpi'}-${Date.now()}.csv`, toCsv(revenueDetails));
  };

  const overview = (
    <div className="grid gap-4">
      <div className="grid gap-4 md:grid-cols-4">
        <KpiCard title="Revenue" value="₹ 4,82,300" trend={{ direction: 'up', value: 12.4 }} onClick={() => openKpi('Revenue', '₹ 4,82,300')} />
        <KpiCard title="Orders" value="1,284" trend={{ direction: 'up', value: 3.1 }} onClick={() => openKpi('Orders', '1,284')} />
        <KpiCard title="Occupancy" value="78%" trend={{ direction: 'down', value: 1.2 }} onClick={() => openKpi('Occupancy', '78%')} />
        <KpiCard title="Rating" value="4.7 / 5" trend={{ direction: 'up', value: 0.2, label: 'vs. last mo.' }} onClick={() => openKpi('Rating', '4.7 / 5')} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <StatusCard title="API" priority="info" description="All systems nominal" />
        <SummaryCard
          items={[
            { label: 'Signups', value: '1,128' },
            { label: 'Active users', value: '982' },
            { label: 'Trials', value: '146' },
            { label: 'Paid', value: '624' },
          ]}
        />
        <WidgetShell title="Traffic">
          <div className="flex items-center gap-4">
            <ProgressRing value={72} />
            <div className="flex-1">
              <MiniSparkline data={spark} />
              <ProgressBar value={64} className="mt-2" />
            </div>
          </div>
        </WidgetShell>
      </div>
    </div>
  );

  const analytics = (
    <div className="grid gap-4">
      <div className="grid gap-4 md:grid-cols-2">
        <WidgetShell title="Revenue trend">
          <BaseAreaChart data={area} areas={[{ dataKey: 'value', name: 'Revenue' }]} xAxisKey="month" height={260} />
        </WidgetShell>
        <WidgetShell title="Orders this week">
          <BaseBarChart data={bar} bars={[{ dataKey: 'value', name: 'Orders' }]} xAxisKey="name" height={260} />
        </WidgetShell>
      </div>
      <WidgetShell title="Traffic sources">
        <BaseDonutChart data={donut} height={280} />
      </WidgetShell>
    </div>
  );

  const reports = (
    <DataGrid<KpiDetail>
      title="Recent transactions"
      data={revenueDetails}
      columns={detailColumns}
      defaultPageSize={10}
    />
  );

  return (
    <div className="grid gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-content-secondary">Welcome back. Here's what's happening today.</p>
      </div>

      <div className="sticky top-14 z-30 -mx-6 bg-surface-app px-6 py-2 backdrop-blur">
        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview', content: overview },
            { id: 'analytics', label: 'Analytics', content: analytics },
            { id: 'reports', label: 'Reports', content: reports },
          ]}
        />
      </div>

      <Modal
        open={!!drill}
        onClose={closeKpi}
        title={drill ? `${drill.title} · ${drill.value}` : ''}
        width={820}
        footer={
          <div className="flex w-full items-center justify-between">
            <ExportButtonGroup onPrint={handlePrint} onExcel={handleExcel} />
            <Button onClick={closeKpi}>Close</Button>
          </div>
        }
      >
        <div ref={printRef}>
          <div className="mb-3 grid grid-cols-3 gap-3 text-sm">
            <div className="rounded-md border border-border p-3">
              <div className="text-xs text-content-muted">Period</div>
              <div className="font-semibold">Apr 2026</div>
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="text-xs text-content-muted">Rows</div>
              <div className="font-semibold">{revenueDetails.length}</div>
            </div>
            <div className="rounded-md border border-border p-3">
              <div className="text-xs text-content-muted">Total</div>
              <div className="font-semibold">
                ₹ {revenueDetails.reduce((s, r) => s + r.amount, 0).toLocaleString('en-IN')}
              </div>
            </div>
          </div>
          <DataGrid<KpiDetail>
            data={revenueDetails}
            columns={detailColumns}
            defaultPageSize={5}
            searchable={false}
            title="Breakdown"
            minHeight={220}
          />
        </div>
      </Modal>
    </div>
  );
}
